// Bring in a client list: pick a file, check the columns, fix typos, import.

import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { api } from '../lib/api';
import { linkClick, navigate } from '../lib/router';
import { Button, Field, Notice, Progress, toastError } from '../lib/ui';
import { Icon } from '../lib/icons';
import { num, plural } from '../lib/format';
import { COLUMN_LABELS, guessColumns, hasHeader, prepareRows, readSheet, type ColumnKind } from '../lib/csv';
import type { ImportResult } from '../../shared/types';
import { WELCOME_TAG } from '../../shared/plan';
import { Butterfly } from '../parts/Butterfly';
import { tapWord } from './Year';

type Step = 'pick' | 'check' | 'going' | 'done';

interface Parsed {
  file: string;
  header: string[];
  rows: string[][];
  cols: ColumnKind[];
}

const CHUNK = 250;

export function Import() {
  const [step, setStep] = useState<Step>('pick');
  const [p, setP] = useState<Parsed | null>(null);
  const [tag, setTag] = useState('');
  const [fixes, setFixes] = useState<Record<number, boolean>>({});
  const [consent, setConsent] = useState(false);
  const [newClients, setNewClients] = useState(false);
  const [welcomeOn, setWelcomeOn] = useState<boolean | null>(null);
  const [listSize, setListSize] = useState<number | null>(null);
  const [done, setDone] = useState(0);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<{ sequences: { trigger_tag: string; active: boolean }[] }>('/api/sequences').then(
      (r) => setWelcomeOn(r.sequences.some((q) => q.trigger_tag === WELCOME_TAG && q.active)),
      () => setWelcomeOn(null),
    );
  }, []);

  const prepared = useMemo(() => (p ? prepareRows(p.header, p.rows, p.cols, fixes) : null), [p, fixes]);

  const onFile = async (f: File) => {
    setError(null);
    try {
      const all = await readSheet(f);
      if (!all.length) throw new Error('That file looks empty.');
      const header = hasHeader(all) ? all[0] : all[0].map((_, i) => `Column ${i + 1}`);
      const rows = hasHeader(all) ? all.slice(1) : all;
      const cols = guessColumns(header, rows.slice(0, 50));
      if (!rows.length) throw new Error('The file has headers but no people under them.');
      setP({ file: f.name, header, rows, cols });
      setFixes({});
      setTag(defaultTag(f.name));
      setStep('check');
    } catch (e) {
      setError((e as Error).message || 'Could not read that file. Save it as CSV or Excel and try again.');
    }
  };

  const go = async () => {
    if (!prepared) return;
    setStep('going');
    setDone(0);
    const total: ImportResult = { added: 0, updated: 0, invalid: 0, keptUnsubscribed: 0 };
    let sent = 0;
    try {
      for (let i = 0; i < prepared.rows.length; i += CHUNK) {
        const r = await api<ImportResult>('/api/contacts/import', { body: { rows: prepared.rows.slice(i, i + CHUNK), tag: tag.trim() || undefined, newClients, source: p!.file.slice(0, 40) } });
        total.added += r.added;
        total.updated += r.updated;
        total.invalid += r.invalid;
        total.keptUnsubscribed += r.keptUnsubscribed;
        sent = Math.min(prepared.rows.length, i + CHUNK);
        setDone(sent);
      }
      total.invalid += prepared.invalid.length;
      const after = await api<{ byStatus: Record<string, number> }>('/api/contacts?page=1').catch(() => null);
      setListSize(after ? after.byStatus.active : null);
      setResult(total);
      setStep('done');
    } catch (e) {
      toastError(e);
      // Rows already sent are saved. Running it again updates them instead of adding twice.
      setResult({ ...total });
      setError(`Stopped after ${num(sent)} people. Importing the same file again is safe; nobody gets added twice.`);
      setStep('check');
    }
  };

  return (
    <div class="wrap narrow-l">
      <a href="/contacts" class="back-link" onClick={(e) => linkClick(e, '/contacts')}>
        <Icon name="back" size={16} /> Contacts
      </a>
      <h1 class="display">Import contacts</h1>

      {step === 'pick' && (
        <>
          <p class="lead">Your Aesthetic Record export, or any spreadsheet.</p>
          <Drop onFile={onFile} />
          {error && <Notice kind="bad">{error}</Notice>}
          <details class="how" open>
            <summary>How to export from Aesthetic Record</summary>
            <ol>
              <li>In Aesthetic Record, open Clients.</li>
              <li>Use Export (top right) and pick CSV or Excel.</li>
              <li>Choose that file above. The app finds the name, email, phone and last visit columns for you.</li>
            </ol>
          </details>
        </>
      )}

      {step === 'check' && p && prepared && (
        <>
          <p class="lead">
            Found <b>{plural(p.rows.length, 'row')}</b> in {p.file}.{' '}
            <button class="link" onClick={() => setStep('pick')}>
              Pick a different file
            </button>
          </p>
          {error && <Notice kind="warn">{error}</Notice>}

          <section class="card">
            <h2 class="card-title">Check the columns</h2>
            <p class="muted small">The app guessed what each column holds. Change any that look wrong.</p>
            <div class="map-grid">
              {p.header.map((h, i) => (
                <div class="map-row" key={i}>
                  <div class="map-src">
                    <b>{h || `Column ${i + 1}`}</b>
                    <small>{p.rows.slice(0, 3).map((r) => r[i]).filter(Boolean).join(' · ') || 'empty'}</small>
                  </div>
                  <Icon name="next" size={16} class="muted" />
                  <select
                    class="select"
                    value={p.cols[i]}
                    aria-label={`What ${h} holds`}
                    onChange={(e) => {
                      const v = e.currentTarget.value as ColumnKind;
                      // One column per kind: picking a kind clears it elsewhere.
                      const cols = p.cols.map((c, j) => (j === i ? v : c === v && v !== 'skip' ? 'skip' : c));
                      setP({ ...p, cols });
                    }}
                  >
                    {(Object.keys(COLUMN_LABELS) as ColumnKind[]).map((k) => (
                      <option key={k} value={k}>
                        {COLUMN_LABELS[k]}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            {!p.cols.includes('email') && <Notice kind="bad">Pick which column has the email address.</Notice>}
          </section>

          {prepared.typos.length > 0 && (
            <section class="card">
              <h2 class="card-title">Possible typos</h2>
              <p class="muted small">These look like small mistakes. Fixed ones get imported with the fix.</p>
              <div class="typos">
                {prepared.typos.map((t) => (
                  <label class="typo" key={t.line}>
                    <input type="checkbox" checked={fixes[t.line] !== false} onChange={(e) => setFixes({ ...fixes, [t.line]: e.currentTarget.checked })} />
                    <span>
                      <s>{t.email}</s> <Icon name="next" size={14} /> <b>{t.fix}</b>
                    </span>
                  </label>
                ))}
              </div>
            </section>
          )}

          <section class="card">
            <h2 class="card-title">Add a tag</h2>
            <Field label="Tag everyone in this file" hint="Optional. Tags let you send to a group later, like 'botox' or 'vip'.">
              <input value={tag} onInput={(e) => setTag(e.currentTarget.value)} placeholder="for example: clients 2026" />
            </Field>
          </section>

          <div class="import-summary">
            <div>
              <b>{num(prepared.rows.length)}</b> people will be imported
              {prepared.dupes > 0 && <span class="muted"> · {plural(prepared.dupes, 'repeat')} merged</span>}
              {prepared.invalid.length > 0 && <span class="muted"> · {plural(prepared.invalid.length, 'row')} without a usable email skipped</span>}
              {prepared.optedOut > 0 && <span class="muted"> · {num(prepared.optedOut)} marked unsubscribed in the file, they won't get emails</span>}
            </div>
            <p class="muted small">People who unsubscribed before stay unsubscribed. Importing again never adds anyone twice.</p>
            <label class="consent">
              <input type="checkbox" checked={newClients} onChange={(e) => setNewClients(e.currentTarget.checked)} />
              <span>These are new clients. Send them the welcome emails.</span>
            </label>
            {welcomeOn === false && <p class="muted small import-welcome-off">The welcome emails are off. Turn them on in Automatic first.</p>}
            <label class="consent">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.currentTarget.checked)} />
              <span>These are my clients, and they gave me their email.</span>
            </label>
            <Button kind="primary" size="l" icon="upload" disabled={!consent || !prepared.rows.length || !p.cols.includes('email')} onClick={go}>
              Import {num(prepared.rows.length)}
            </Button>
          </div>
        </>
      )}

      {step === 'going' && prepared && (
        <section class="card going">
          <Icon name="upload" size={28} />
          <h2>Importing…</h2>
          <Progress value={done} max={prepared.rows.length} label="Import progress" />
          <p class="muted">
            {num(done)} of {num(prepared.rows.length)}
          </p>
        </section>
      )}

      {step === 'done' && result && (
        <section class="card done-card">
          <div class="welcome-check">
            <Butterfly size={36} dark open />
          </div>
          <h2 class="display">{result.added > 0 ? `${plural(result.added, 'client')} added.` : 'No new clients.'}</h2>
          {listSize != null && (
            <p class="lead">
              Your list is now <b>{num(listSize)}</b>.
            </p>
          )}
          {(result.updated > 0 || result.keptUnsubscribed > 0 || result.invalid > 0) && (
            <p class="muted small">
              {[
                result.updated > 0 && `${num(result.updated)} updated`,
                result.keptUnsubscribed > 0 && `${num(result.keptUnsubscribed)} won't get emails`,
                result.invalid > 0 && `${num(result.invalid)} skipped`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )}
          <div class="done-actions">
            <Button onClick={() => navigate('/contacts')}>See contacts</Button>
            <Button kind="primary" icon="mail" onClick={() => navigate('/')}>
              Write an email
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}

function Drop({ onFile }: { onFile: (f: File) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const touch = tapWord() === 'Tap';
  return (
    <div
      class={`drop drop-big ${over ? 'over' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const f = e.dataTransfer?.files[0];
        if (f) onFile(f);
      }}
      onClick={() => input.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && input.current?.click()}
    >
      <div class="drop-icon">
        <Icon name="file" size={30} />
      </div>
      <b>{touch ? 'Tap to choose your file' : 'Drop your file here, or click to choose'}</b>
      <span class="muted">CSV or Excel</span>
      <input
        ref={input}
        type="file"
        accept=".csv,.txt,.xlsx,.xls,.ods,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        hidden
        onChange={(e) => {
          const f = e.currentTarget.files?.[0];
          if (f) onFile(f);
          e.currentTarget.value = '';
        }}
      />
    </div>
  );
}

function defaultTag(file: string): string {
  return /aesthetic|ar[-_ ]?export|clients?/i.test(file) ? 'clients' : '';
}
